import React from 'react';
import { Link } from 'react-router-dom';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-navy-900 via-navy-800 to-cyan-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block bg-cyan-400/20 backdrop-blur-sm border border-cyan-400/40 rounded-full px-6 py-2 mb-6">
              <span className="text-sm font-bold tracking-wide">OUR STORY</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black leading-none mb-6">
              About <span className="text-cyan-400">Shiny Crew</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Excellence in cleaning and maintenance services since [Year]. Building trust, one sparkling space at a time.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-navy-900 mb-6">
                Our <span className="text-cyan-500">Story</span>
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Shiny Crew Cleaning was founded with a simple mission: to provide exceptional cleaning and maintenance services that transform spaces and simplify lives. What started as a small local operation has grown into a trusted name serving thousands of satisfied customers across the area.
                </p>
                <p>
                  We believe that a clean space isn't just about appearances—it's about creating environments where people can thrive, businesses can succeed, and homes can be true sanctuaries. Every member of our team shares this commitment to excellence, approaching each job with professionalism, attention to detail, and genuine care.
                </p>
                <p>
                  Over the years, we've expanded our services to meet the diverse needs of our community. From residential cleaning to commercial maintenance, locksmith services to handyman work, we've built a comprehensive suite of solutions designed to make property management effortless for our clients.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-navy-900 mb-4">
              Our <span className="text-cyan-500">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Excellence',
                description: 'We never settle for good enough. Every job is an opportunity to exceed expectations and deliver outstanding results.',
                icon: '⭐',
                color: 'from-cyan-400 to-cyan-600',
              },
              {
                title: 'Integrity',
                description: 'Honesty and transparency are at the core of our business. We do what we say we\'ll do, when we say we\'ll do it.',
                icon: '🛡️',
                color: 'from-navy-700 to-navy-900',
              },
              {
                title: 'Reliability',
                description: 'You can count on us. From punctuality to consistency, we build trust through dependable service every time.',
                icon: '✓',
                color: 'from-cyan-500 to-navy-700',
              },
              {
                title: 'Innovation',
                description: 'We embrace new technologies and methods that improve our service quality and environmental sustainability.',
                icon: '💡',
                color: 'from-coral-400 to-coral-500',
              },
              {
                title: 'Community',
                description: 'We\'re proud to serve our local community and committed to making a positive impact beyond just cleaning.',
                icon: '❤️',
                color: 'from-navy-800 to-cyan-500',
              },
              {
                title: 'Safety',
                description: 'The wellbeing of our team and clients is paramount. We maintain the highest safety and security standards.',
                icon: '🔒',
                color: 'from-cyan-400 to-navy-800',
              },
            ].map((value, index) => (
              <div key={index} className="relative group">
                <div className={`absolute inset-0 bg-gradient-to-br ${value.color} rounded-2xl opacity-5 group-hover:opacity-10`}></div>
                <div className="relative bg-white rounded-2xl p-8 border-2 border-gray-200 group-hover:border-cyan-400 shadow-lg h-full">
                  <div className="text-5xl mb-4">{value.icon}</div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-navy-900 mb-6">
                Meet Our <span className="text-cyan-500">Team</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Behind every sparkling clean space is a dedicated professional who takes pride in their work. Our team members are fully trained, background-checked, and insured. But more importantly, they're passionate about what they do.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                {[
                  { title: 'Fully Trained', desc: 'Ongoing professional development and certification programs', icon: '🎓' },
                  { title: 'Background Checked', desc: 'Thorough vetting process for your peace of mind', icon: '✓' },
                  { title: 'Insured & Bonded', desc: 'Complete protection for you and your property', icon: '🛡️' },
                ].map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                    <div className="text-4xl mb-3">{item.icon}</div>
                    <h3 className="text-xl font-bold text-navy-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-cyan-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              By The <span className="text-cyan-400">Numbers</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { number: '10+', label: 'Years Experience', icon: '📅' },
              { number: '5,000+', label: 'Happy Clients', icon: '😊' },
              { number: '50+', label: 'Team Members', icon: '👥' },
              { number: '98%', label: 'Satisfaction Rate', icon: '⭐' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl mb-3">{stat.icon}</div>
                <div className="text-4xl md:text-5xl font-black text-cyan-400 mb-2">{stat.number}</div>
                <div className="text-gray-300 font-semibold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-400 to-cyan-600">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Ready to Experience the Shiny Crew Difference?
            </h2>
            <p className="text-xl text-navy-900 font-semibold mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied customers who trust us with their spaces
            </p>
            <Link to="/get-quote" className="inline-block px-12 py-5 bg-navy-900 text-white font-black text-lg rounded-xl hover:bg-navy-800 shadow-2xl">
              GET STARTED TODAY
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
